import { MessageSquare, SquareArrowOutUpRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

/**
 * Shared header for the Agent-thread widget cards: an optional date line above
 * the task reference (both in one left-aligned container so the title stays
 * put), plus up to two top-right icon buttons — jump the driver chat to this
 * task's conversation, and open the task in the widget panel.
 */
export type WidgetCardHeaderProps = {
  title: string
  /** A short line shown above the title — a date, or "Aktualizacja · …". */
  dateLabel?: string
  /** Makes the title a link that opens the task's document (a PDF scan). */
  onOpenDocument?: () => void
  /** Jumps the driver chat to the conversation about this task. */
  onChatWithDriver?: () => void
  /** Opens the task in the widget panel (the "tools" window). */
  onOpenInPanel?: () => void
}

export const WidgetCardHeader = ({
  title,
  dateLabel,
  onOpenDocument,
  onChatWithDriver,
  onOpenInPanel,
}: WidgetCardHeaderProps) => {
  const { t } = useTranslation()
  return (
    <CardHeader className="flex-row items-start gap-2 space-y-0 p-4 pb-3">
      <div className="min-w-0 flex-1">
        {dateLabel && (
          <p className="mb-1 truncate text-xs font-medium text-muted-foreground">
            {dateLabel}
          </p>
        )}
        <CardTitle className="text-base">
          {onOpenDocument ? (
            <button
              type="button"
              onClick={onOpenDocument}
              title={t("chat.widgetCard.openDocumentScan")}
              className="block max-w-full truncate rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {title}
            </button>
          ) : (
            <span className="block truncate">{title}</span>
          )}
        </CardTitle>
      </div>
      {(onChatWithDriver || onOpenInPanel) && (
        <TooltipProvider>
          {/* `!-mt-1.5` wins over CardHeader's `space-y-0`, so the buttons sit
            the same 10px from the top as from the right edge. */}
          <div className="-m-1.5 !-mt-1.5 flex shrink-0 items-center">
            {onChatWithDriver && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={onChatWithDriver}
                    aria-label={t("chat.widgetCard.chatWithDriver")}
                    className="text-muted-foreground"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {t("chat.widgetCard.chatWithDriver")}
                </TooltipContent>
              </Tooltip>
            )}
            {onOpenInPanel && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={onOpenInPanel}
                    aria-label={t("chat.widgetCard.openInPanel")}
                    className="text-muted-foreground"
                  >
                    <SquareArrowOutUpRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {t("chat.widgetCard.openInPanel")}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      )}
    </CardHeader>
  )
}

export default WidgetCardHeader
